import {
  Activity,
  ArrowRightLeft,
  Baby,
  Calculator,
  Droplets,
  FlaskConical,
  Heart,
  Pill,
  Scale,
  Stethoscope,
} from 'lucide-react';
import type { CalculatorCategory } from '../../contracts';

// Map calculator IDs to categories for visual styling
export const CALCULATOR_CATEGORIES: Record<string, CalculatorCategory> = {
  // Risk scores
  wells_dvt: 'risk_score',
  wells_pe: 'risk_score',
  chadsvasc: 'risk_score',
  cha2ds2_vasc: 'risk_score',
  hasbled: 'risk_score',
  heart_score: 'risk_score',
  framingham: 'risk_score',
  ascvd: 'risk_score',
  canadian_cspine: 'risk_score',
  padua: 'risk_score',
  grace_acs: 'risk_score',
  stop_bang: 'risk_score',
  // Renal
  egfr: 'renal',
  egfr_ckd_epi: 'renal',
  egfr_ckd_epi_2021: 'renal',
  creatinine_clearance: 'renal',
  cockcroft_gault: 'renal',
  fena: 'renal',
  // Body measurements
  bmi: 'body_measurement',
  bsa: 'body_measurement',
  ibw: 'body_measurement',
  blood_volume_calc: 'body_measurement',
  // Lab values
  anion_gap: 'lab_value',
  corrected_calcium: 'lab_value',
  sodium_correction: 'lab_value',
  fib4: 'lab_value',
  // Cardiac
  qtc: 'cardiac',
  qtc_bazett: 'cardiac',
  map: 'cardiac',
  // Severity
  curb65: 'severity',
  curb_65: 'severity',
  gcs: 'severity',
  glasgow: 'severity',
  apache: 'severity',
  sofa: 'severity',
  meld: 'severity',
  phq9: 'severity',
  gad7: 'severity',
  dasi: 'severity',
  columbia_ssrs: 'risk_score',
  race_scale: 'severity',
  // Newly verified stateful calculators
  ada_diabetes_risk: 'risk_score',
  adhere_algorithm: 'severity',
  ascod_algorithm: 'severity',
  bpp_score: 'obgyn',
  crafft: 'risk_score',
  ctcae: 'severity',
  edacs: 'risk_score',
  findrisc: 'risk_score',
  hct_ci: 'severity',
  licurse_score: 'risk_score',
  local_anesthetic_dosing: 'drug_dosing',
  mipss70: 'risk_score',
  nafcs: 'severity',
  opioid_risk_tool: 'risk_score',
  perioperative_anticoagulation: 'drug_dosing',
  pss3_safety: 'risk_score',
  rbc_units_screen: 'lab_value',
  respiratory_score_asthma: 'severity',
  reynolds_risk: 'risk_score',
  slicc_sle: 'severity',
  wast: 'risk_score',
  years_algorithm: 'risk_score',
  // Drug dosing
  mme: 'drug_dosing',
  drug_dose: 'drug_dosing',
  paracetamol_dose: 'drug_dosing',
  // Conversions
  temp_conversion: 'conversion',
  // OB/GYN
  edd: 'obgyn',
  gestational_age: 'obgyn',
};

// Get category from calculator ID (with fallback)
export const getCalculatorCategory = (calculator: string): CalculatorCategory => {
  // Try exact match first
  if (calculator in CALCULATOR_CATEGORIES) {
    return CALCULATOR_CATEGORIES[calculator] as CalculatorCategory;
  }
  // Try prefix matching for calculators like "wells_dvt_score" -> "wells"
  const prefix = calculator.split('_')[0];
  if (prefix && prefix in CALCULATOR_CATEGORIES) {
    return CALCULATOR_CATEGORIES[prefix] as CalculatorCategory;
  }
  // Check if any key is contained in the calculator name
  const keys = Object.keys(CALCULATOR_CATEGORIES);
  for (const key of keys) {
    if (calculator.includes(key) && key in CALCULATOR_CATEGORIES) {
      return CALCULATOR_CATEGORIES[key] as CalculatorCategory;
    }
  }
  return 'other';
};

// Category-based icon and color configuration
export const CATEGORY_CONFIG: Record<
  CalculatorCategory,
  { icon: typeof Calculator; colorClass: string; bgClass: string }
> = {
  risk_score: { icon: Heart, colorClass: 'text-accent-red', bgClass: 'bg-accent-red/10' },
  renal: { icon: Droplets, colorClass: 'text-accent-blue', bgClass: 'bg-accent-blue/10' },
  body_measurement: {
    icon: Scale,
    colorClass: 'text-accent-green',
    bgClass: 'bg-accent-green/10',
  },
  lab_value: {
    icon: FlaskConical,
    colorClass: 'text-accent-purple',
    bgClass: 'bg-accent-purple/10',
  },
  cardiac: {
    icon: Activity,
    colorClass: 'text-accent-pink',
    bgClass: 'bg-accent-pink/10',
  },
  severity: {
    icon: Stethoscope,
    colorClass: 'text-accent-orange',
    bgClass: 'bg-accent-orange/10',
  },
  drug_dosing: {
    icon: Pill,
    colorClass: 'text-accent-yellow',
    bgClass: 'bg-accent-yellow/10',
  },
  conversion: {
    icon: ArrowRightLeft,
    colorClass: 'text-text-secondary',
    bgClass: 'bg-surface-hover',
  },
  obgyn: { icon: Baby, colorClass: 'text-accent-pink', bgClass: 'bg-accent-pink/10' },
  other: {
    icon: Calculator,
    colorClass: 'text-text-secondary',
    bgClass: 'bg-surface-hover',
  },
};

// Format a snake_case key to Title Case label
export const formatInputLabel = (key: string): string => {
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Format input value for display
export const formatInputValue = (value: string | number | boolean): string => {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return String(value);
};

// Get the primary score/value from result
export const getPrimaryValue = (result: {
  score?: number;
  value?: number;
  unit?: string;
  dose?: number;
  single_dose?: number;
  dose_unit?: string;
  max_score?: number;
}): { value: number; unit: string } | null => {
  // Drug dose calculators use dose/dose_unit or single_dose/dose_unit
  const doseValue = result.dose ?? result.single_dose;
  if (typeof doseValue === 'number' && result.dose_unit) {
    return { value: doseValue, unit: result.dose_unit };
  }
  // Value-based calculators (eGFR, BMI, etc.) use value/unit
  if (typeof result.value === 'number' && result.unit) {
    return { value: result.value, unit: result.unit };
  }
  // Formula calculators that use score with a unit (e.g., Creatinine Clearance returns score with unit: "mL/min")
  if (typeof result.score === 'number' && result.unit) {
    return { value: result.score, unit: result.unit };
  }
  // Pure scoring calculators use score, display with "points" or "/ max points"
  if (typeof result.score === 'number') {
    const unit = result.max_score ? `/ ${result.max_score} points` : 'points';
    return { value: result.score, unit };
  }
  return null;
};

// Get inputs that are true (for boolean scoring calculators)
export const getTrueInputs = (
  inputs?: Record<string, string | number | boolean>
): Array<[string, boolean]> => {
  if (!inputs) return [];
  return Object.entries(inputs).filter(([, value]) => value === true) as Array<
    [string, boolean]
  >;
};

// Check if inputs are boolean-based (scoring calculator)
export const isBooleanInputs = (
  inputs?: Record<string, string | number | boolean>
): boolean => {
  if (!inputs) return false;
  const values = Object.values(inputs);
  return values.length > 0 && values.every((v) => typeof v === 'boolean');
};

// Risk category keywords mapped to validation color tokens
const RISK_COLOR_MAP: Record<string, string> = {
  normal: 'text-text-primary',
  low: 'text-validation-destructive',
  mild: 'text-validation-destructive',
  moderate: 'text-validation-destructive',
  intermediate: 'text-validation-destructive',
  high: 'text-validation-destructive',
  critical: 'text-validation-destructive',
  severe: 'text-validation-destructive',
  'very high': 'text-validation-destructive',
};

/**
 * Map a risk_category string to a Tailwind text-color class.
 * Falls back to `text-text-primary` when the category is absent or unrecognised.
 */
export const getScoreColorClass = (riskCategory?: string): string => {
  if (!riskCategory) return 'text-text-primary';
  const normalised = riskCategory.toLowerCase().trim();
  return RISK_COLOR_MAP[normalised] ?? 'text-text-primary';
};
